import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { 
  faSearch, faPlus, faEdit, faTrash, faTimes, faCheck, 
  faQuestionCircle, faTag, faArrowLeft, faPaintBrush, faFilter, faEllipsisV, faSpinner, faExclamationTriangle, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { TagsService, Tag, CreateTagRequest } from '../../services/tags.service';
import { TenantService } from '../../services/tenant.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { HeaderActionsComponent } from '../../components/header-actions/header-actions.component';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { ItemListComponent, ListConfig, ListItem } from '../../components/item-list/item-list.component';
import { ButtonComponent } from '../../components/button/button.component';
import { DateFormatterService } from '../../services/date-formatter.service';
import { normalizeForSearch } from '../../utils/text-search.util';

@Component({
  selector: 'app-tags',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    ModalDeleteConfirmComponent,
    ItemListComponent,
    ButtonComponent
],
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss']
})
export class TagsComponent implements OnInit, OnDestroy {
  tags: Tag[] = [];
  filteredtags: Tag[] = [];
  searchTerm: string = '';
  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;
  selectedtag: Tag | null = null;
  selectedDropdown: string | null = null;
  nameError: string = '';
  isSubmitting: boolean = false;
  loading: boolean = false;
  
  tagForm: CreateTagRequest = {
    name: '',
    description: '',
    text_color: '#FFFFFF',
    bg_color: '#FF009A'
  };

  predefinedColors: { name: string; bg_color: string; text_color: string }[] = [];
  
  private tagsSubscription?: Subscription;

  listConfig: ListConfig = {
    itemIcon: faTag,
    showMetadata: true,
    showType: false,
    emptyMessage: 'TAGS.EMPTY.MESSAGE',
    emptyMessageFiltered: 'TAGS.NO_RESULTS.MESSAGE',
    actions: [
      {
        id: 'edit',
        label: 'BUTTON.EDIT',
        icon: faEdit,
        cssClass: 'edit-item'
      },
      {
        id: 'delete',
        label: 'BUTTON.DELETE',
        icon: faTrash,
        cssClass: 'delete-item'
      }
    ]
  };

  headerConfig = {
    addButton: {
      type: 'add' as const,
      text: 'BUTTON.CREATE',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    showBackButton: true,
    searchConfig: {
      showTextSearch: true,
      showTagFilter: false,
      textPlaceholder: 'SEARCH.PLACEHOLDER.TAGS',
      tagPlaceholder: ''
    }
  };

  get itemsForList(): ListItem[] {
    return this.filteredtags.map(tag => ({
      id: tag.id,
      name: tag.name,
      tag: {
        id: tag.id,
        name: tag.name,
        bg_color: tag.bg_color,
        text_color: tag.text_color
      },
      created_date: tag.created_at ? this.dateFormatter.formatDate(tag.created_at) : '',
      created_time: tag.created_at ? this.dateFormatter.formatTime(tag.created_at) : ''
    }));
  }

  get hasActiveFilters(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  faSearch = faSearch;
  faPlus = faPlus;
  faEdit = faEdit;
  faTrash = faTrash;
  faTimes = faTimes;
  faCheck = faCheck;
  faQuestionCircle = faQuestionCircle;
  faTag = faTag;
  faArrowLeft = faArrowLeft;
  faPaintBrush = faPaintBrush;
  faFilter = faFilter;
  faEllipsisV = faEllipsisV;
  faSpinner = faSpinner;
  faExclamationTriangle = faExclamationTriangle;
  faInfoCircle = faInfoCircle;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private tagsService: TagsService,
    private alertService: AlertService,
    private dateFormatter: DateFormatterService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.loadtags();
    this.predefinedColors = this.tagsService.getPredefinedColors();
    
    this.tagsSubscription = this.tagsService.tags$.subscribe((tags: Tag[]) => {
      this.tags = tags;
      this.filtertags();
    });
  }

  onHeaderSearch(filters: { searchTerm: string }) {
    this.searchTerm = filters.searchTerm;
    this.onSearch();
  }

  ngOnDestroy(): void {
    if (this.tagsSubscription) {
      this.tagsSubscription.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    this.selectedDropdown = null;
  }

  loadtags(): void {
    this.tagsService.getTags().subscribe({
      next: () => {
      },
      error: (err: any) => {
        this.alertService.showError(err);
      }
    });
  }

  onSearch(): void {
    this.filtertags();
  }

  filtertags(): void {
    if (!this.searchTerm.trim()) {
      this.filteredtags = [...this.tags];
      return;
    }
    
    const term = normalizeForSearch(this.searchTerm);
    this.filteredtags = this.tags.filter(tag =>
      normalizeForSearch(tag.name).includes(term)
    );
  }

  toggleDropdown(event: Event, tagId: string): void {
    event.stopPropagation();
    this.selectedDropdown = this.selectedDropdown === tagId ? null : tagId;
  }

  openCreateModal(): void {
    this.tagForm = {
      name: '',
      description: '',
      text_color: '#FFFFFF',
      bg_color: '#FF009A'
    };
    this.selectedDropdown = null;
    this.showCreateModal = true;
  }

  openEditModal(tag: Tag): void {
    this.selectedtag = tag;
    this.tagForm = {
      name: tag.name,
      description: tag.description || '',
      text_color: tag.text_color,
      bg_color: tag.bg_color
    };
    this.selectedDropdown = null;
    this.showEditModal = true;
  }

  openDeleteModal(tag: Tag): void {
    this.selectedtag = tag;
    this.selectedDropdown = null;
    this.showDeleteModal = true;
  }

  onItemClick(item: ListItem): void {
    
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    const tag = this.tags.find(t => t.id === event.item.id);
    if (!tag) return;

    switch (event.action) {
      case 'edit':
        this.openEditModal(tag);
        break;
      case 'delete':
        this.openDeleteModal(tag);
        break;
    }
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showEditModal = false;
    this.showDeleteModal = false;
    this.selectedtag = null;
    this.selectedDropdown = null;
    this.nameError = '';
    this.isSubmitting = false;
  }

  selectColor(color: { bg_color: string; text_color: string }): void {
    this.tagForm.bg_color = color.bg_color;
    this.tagForm.text_color = color.text_color;
  }

  createtag(): void {
    if (this.isSubmitting) return;
    this.nameError = '';
    
    const errors = this.tagsService.validateTagData(this.tagForm);
    if (errors.length > 0) {
      const duplicateError = errors.find((error: string) => error.includes(this.translate.instant('TAGS.ERRORS.DUPLICATE')));
      if (duplicateError) {
        this.nameError = duplicateError;
      }
      
      this.alertService.showError(errors.join('\n'));
      return;
    }

    this.isSubmitting = true;

    this.tagsService.createTag(this.tagForm).subscribe({
      next: () => {
        this.closeModals();
        this.isSubmitting = false;
      },
      error: (err: any) => {
        if (err.error?.message?.includes('já existe') || 
            err.error?.message?.includes('duplicate') ||
            err.status === 409) {
          this.nameError = this.translate.instant('TAGS.ERRORS.DUPLICATE');
        } else {
          this.alertService.showError(err);
        }
        
        this.isSubmitting = false;
      }
    });
  }

  updatetag(): void {
    if (!this.selectedtag || this.isSubmitting) return;

    this.nameError = '';

    const errors = this.tagsService.validateTagData(this.tagForm, this.selectedtag.id);
    if (errors.length > 0) {
      const duplicateError = errors.find((error: string) => error.includes('Já existe uma etiqueta'));
      if (duplicateError) {
        this.nameError = duplicateError;
      }

      this.alertService.showError(errors.join('\n'));
      return;
    }

    this.isSubmitting = true;

    this.tagsService.updateTag(this.selectedtag.id, this.tagForm).subscribe({
      next: () => {
        this.closeModals();
        this.isSubmitting = false;
      },
      error: (err: any) => {
        if (err.error?.message?.includes('já existe') || 
            err.error?.message?.includes('duplicate') ||
            err.status === 409) {
          this.nameError = this.translate.instant('TAGS.ERRORS.DUPLICATE');
        } else {
          this.alertService.showError(err);
        }
        
        this.isSubmitting = false;
      }
    });
  }

  onNameChange(): void {
    if (this.nameError) {
      this.nameError = '';
    }
    
    if (this.tagForm.name.trim()) {
      const excludeId = this.showEditModal ? this.selectedtag?.id : undefined;
      if (this.tagsService.tagNameExists(this.tagForm.name.trim(), excludeId)) {
        this.nameError = this.translate.instant('TAGS.ERRORS.DUPLICATE');
      }
    }
  }

  deletetag(): void {
    if (!this.selectedtag) return;

    this.tagsService.deleteTag(this.selectedtag.id).subscribe({
      next: () => {
        this.closeModals();
      },
      error: (err: any) => {
        this.alertService.showError(err);
      }
    });
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }

  trackById(index: number, item: Tag): string {
    return item.id;
  }
}